import { useState, useEffect, useCallback } from 'react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Timeslot {
  start_time: string; // "YYYY-MM-DD HH:mm:ss"
  end_time: string;
}

interface FormState {
  customer_name: string;
  customer_phone: string;
  service_type: string;
  notes: string;
}

interface BookingCalendarProps {
  timeslotsApi?: string;
  appointmentsApi?: string;
  texts?: Record<string, string>;
  containerStyle?: React.CSSProperties;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DAYS_TR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const SERVICE_OPTIONS = [
  'Sıhhi Tesisat',
  'Tamirat & Tadilat',
  'Kombi Bakımı & Petek Temizliği',
  'Doğalgaz İç Tesisat',
  'Kanal Açma',
  'Musluk & Armatür Tamiratı',
  'Arıza Tespiti & Cihaz Onarımı',
  'Kooperatif & Apartman İşleri',
  'Yangın Sistemleri',
  'Diğer',
];

const API_TIMESLOTS =
  'https://readdy.ai/api/public/calendar/timeslots/1e2638a4-47fe-42e7-b429-415fbb032071.d5b44edd2c7e86c4faa2e036a2b56a12bf11ed14c4537b28cf022c7eeb93d884';
const API_APPOINTMENTS =
  'https://readdy.ai/api/public/calendar/appointments/1e2638a4-47fe-42e7-b429-415fbb032071.d5b44edd2c7e86c4faa2e036a2b56a12bf11ed14c4537b28cf022c7eeb93d884';

function extractDate(dt: string): string {
  return dt.split(' ')[0]; // "YYYY-MM-DD"
}

function extractTime(dt: string): string {
  return dt.split(' ')[1]?.slice(0, 5) ?? ''; // "HH:mm"
}

function getTodayStr(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstWeekday(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function findClosestDate(availableDates: string[]): string | null {
  if (availableDates.length === 0) return null;
  const today = getTodayStr();
  const future = availableDates.filter((d) => d >= today);
  if (future.length > 0) return future[0];
  return availableDates[availableDates.length - 1];
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BookingCalendar({
  timeslotsApi = API_TIMESLOTS,
  appointmentsApi = API_APPOINTMENTS,
  containerStyle,
}: BookingCalendarProps) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [calYear, setCalYear] = useState<number>(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState<number>(new Date().getMonth());

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);

  const [form, setForm] = useState<FormState>({
    customer_name: '',
    customer_phone: '',
    service_type: '',
    notes: '',
  });

  // ─── Fetch timeslots ────────────────────────────────────────────────────────

  const fetchTimeslots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(timeslotsApi);
      if (!res.ok) throw new Error('API hatası');
      const data: Timeslot[] = await res.json();
      setTimeslots(data);

      // Navigate calendar to closest date
      const dates = [...new Set(data.map((s) => extractDate(s.start_time)))].sort();
      const closest = findClosestDate(dates);
      if (closest) {
        const [y, m] = closest.split('-').map(Number);
        setCalYear(y);
        setCalMonth(m - 1);
        if (!selectedDate) setSelectedDate(closest);
      }
    } catch {
      setError('Müsait tarihler yüklenirken hata oluştu. Lütfen sayfayı yenileyin.');
    } finally {
      setLoading(false);
    }
  }, [timeslotsApi]);

  useEffect(() => {
    fetchTimeslots();
  }, [fetchTimeslots]);

  // ─── Derived data ────────────────────────────────────────────────────────────

  const availableDateSet = new Set(timeslots.map((s) => extractDate(s.start_time)));

  const slotsForDate = selectedDate
    ? timeslots.filter((s) => extractDate(s.start_time) === selectedDate)
    : [];

  // ─── Calendar grid ───────────────────────────────────────────────────────────

  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstWeekday = getFirstWeekday(calYear, calMonth);
  const calCells: (number | null)[] = [
    ...Array<null>(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (calCells.length % 7 !== 0) calCells.push(null);

  const monthLabel = `${MONTHS_TR[calMonth]} ${calYear}`;

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const formatCellDate = (day: number) => {
    const mm = String(calMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${calYear}-${mm}-${dd}`;
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleDateSelect = (dateStr: string) => {
    if (!availableDateSet.has(dateStr)) return;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setStep(1);
  };

  const handleSlotSelect = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const handleModifyTime = () => {
    setStep(0);
  };

  const handleFormChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true);
    setError(null);

    const extensions: Record<string, string> = {};
    if (form.service_type) extensions.service_type = form.service_type;

    const body = {
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      start_time: selectedSlot.start_time,
      end_time: selectedSlot.end_time,
      notes: form.notes
        ? `${form.notes}${Object.keys(extensions).length ? ` | ${JSON.stringify(extensions)}` : ''}`
        : Object.keys(extensions).length
        ? JSON.stringify(extensions)
        : '',
    };

    try {
      const res = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Randevu oluşturulamadı');
      setSubmitted(true);
      setSelectedDate(null);
      setSelectedSlot(null);
      setForm({ customer_name: '', customer_phone: '', service_type: '', notes: '' });
      setStep(0);
      await fetchTimeslots();
    } catch {
      setError('Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step indicator ──────────────────────────────────────────────────────────

  const steps = ['Tarih Seçin', 'Saat Seçin', 'Bilgileri Girin'];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={containerStyle} className="w-full">
      {/* Success Banner */}
      {submitted && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-5 flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full shrink-0">
            <i className="ri-check-line text-green-600 text-lg"></i>
          </div>
          <div>
            <p className="text-green-800 font-bold text-sm">Randevunuz Alındı!</p>
            <p className="text-green-700 text-sm mt-0.5">En kısa sürede sizi arayacağız. Vanpolat Tesisat olarak teşekkür ederiz.</p>
          </div>
          <button onClick={() => setSubmitted(false)} className="ml-auto w-6 h-6 flex items-center justify-center text-green-500 hover:text-green-700 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500"></i>
          <p className="text-red-700 text-sm flex-1">{error}</p>
          <button onClick={() => setError(null)} className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Step Indicator */}
      <div className="flex items-center gap-0 mb-7">
        {steps.map((label, i) => (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className={`flex items-center gap-2 ${i <= step ? 'text-blue-700' : 'text-gray-400'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                i < step ? 'bg-blue-600 border-blue-600 text-white' :
                i === step ? 'bg-white border-blue-600 text-blue-700' :
                'bg-white border-gray-200 text-gray-400'
              }`}>
                {i < step ? <i className="ri-check-line"></i> : i + 1}
              </div>
              <span className="text-xs font-semibold whitespace-nowrap hidden sm:block">{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 rounded-full transition-all ${i < step ? 'bg-blue-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* ── STEP 0: Calendar ─────────────────────────────────────────────── */}
      {step === 0 && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <button onClick={prevMonth} className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer text-gray-600 transition-colors">
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <p className="text-base font-bold text-gray-800">{monthLabel}</p>
            <button onClick={nextMonth} className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer text-gray-600 transition-colors">
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-500 text-sm">Müsait tarihler yükleniyor...</p>
            </div>
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS_TR.map((d) => (
                  <div key={d} className="text-center text-xs font-bold text-gray-400 py-1.5">{d}</div>
                ))}
              </div>
              {/* Day cells */}
              <div className="grid grid-cols-7 gap-1">
                {calCells.map((day, idx) => {
                  if (day === null) return <div key={idx} />;
                  const dateStr = formatCellDate(day);
                  const isAvailable = availableDateSet.has(dateStr);
                  const isSelected = selectedDate === dateStr;
                  const isToday = dateStr === getTodayStr();
                  const isPast = dateStr < getTodayStr();

                  return (
                    <button
                      key={idx}
                      onClick={() => handleDateSelect(dateStr)}
                      disabled={!isAvailable}
                      className={`relative aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-semibold transition-all cursor-pointer
                        ${isSelected ? 'bg-blue-600 text-white' :
                          isAvailable ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200' :
                          isPast ? 'text-gray-300 cursor-not-allowed' :
                          'text-gray-300 cursor-not-allowed'}
                      `}
                    >
                      {day}
                      {isToday && !isSelected && (
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-400"></span>
                      )}
                      {isAvailable && !isSelected && (
                        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span> Müsait</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block"></span> Seçili</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STEP 1: Time Slots ─────────────────────────────────────────────── */}
      {step === 1 && (
        <div>
          <button onClick={() => setStep(0)} className="flex items-center gap-1.5 text-blue-600 text-sm font-semibold mb-5 hover:text-blue-800 cursor-pointer transition-colors">
            <i className="ri-arrow-left-line"></i> Tarihe Dön
          </button>
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
              <i className="ri-calendar-check-line text-blue-600"></i>
            </div>
            <div>
              <p className="text-xs text-gray-500">Seçilen Tarih</p>
              <p className="text-sm font-bold text-gray-800">{selectedDate}</p>
            </div>
          </div>
          <p className="text-sm font-semibold text-gray-700 mb-3">Uygun saatlerden birini seçin:</p>
          {slotsForDate.length === 0 ? (
            <div className="py-10 text-center">
              <i className="ri-time-line text-3xl text-gray-300"></i>
              <p className="text-gray-500 text-sm mt-2">Bu tarih için müsait saat bulunamadı.</p>
              <button onClick={() => setStep(0)} className="mt-4 text-blue-600 text-sm font-semibold hover:text-blue-800 cursor-pointer">
                Başka tarih seç
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {slotsForDate.map((slot, i) => {
                const startT = extractTime(slot.start_time);
                const endT = extractTime(slot.end_time);
                return (
                  <button
                    key={i}
                    onClick={() => handleSlotSelect(slot)}
                    className="flex flex-col items-center justify-center border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 rounded-xl px-3 py-3.5 transition-all cursor-pointer group"
                  >
                    <i className="ri-time-line text-blue-500 text-base mb-1 group-hover:text-blue-700"></i>
                    <span className="text-blue-800 font-bold text-sm whitespace-nowrap">{startT}</span>
                    <span className="text-blue-500 text-xs mt-0.5 whitespace-nowrap">— {endT}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── STEP 2: Review + Form ─────────────────────────────────────────────── */}
      {step === 2 && selectedSlot && (
        <form onSubmit={handleSubmit} data-readdy-form id="vanpolat-booking-form">
          {/* Review section */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Seçilen Randevu</p>
              <button
                type="button"
                onClick={handleModifyTime}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 cursor-pointer transition-colors"
              >
                <i className="ri-edit-line"></i> Saati Değiştir
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-blue-500">Tarih</p>
                <p className="text-sm font-bold text-blue-900 mt-0.5">{selectedDate}</p>
              </div>
              <div>
                <p className="text-xs text-blue-500">Başlangıç Saati</p>
                <p className="text-sm font-bold text-blue-900 mt-0.5">{extractTime(selectedSlot.start_time)}</p>
              </div>
              <div>
                <p className="text-xs text-blue-500">Bitiş Saati</p>
                <p className="text-sm font-bold text-blue-900 mt-0.5">{extractTime(selectedSlot.end_time)}</p>
              </div>
              <div>
                <p className="text-xs text-blue-500">Durum</p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full px-2 py-0.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span> Müsait
                </span>
              </div>
            </div>
          </div>

          {/* Form fields */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5" htmlFor="bc-name">
                Ad Soyad <span className="text-red-500">*</span>
              </label>
              <input
                id="bc-name"
                type="text"
                name="customer_name"
                required
                value={form.customer_name}
                onChange={(e) => handleFormChange('customer_name', e.target.value)}
                placeholder="Adınız ve soyadınız"
                className="w-full text-sm px-3.5 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5" htmlFor="bc-phone">
                Telefon Numarası <span className="text-red-500">*</span>
              </label>
              <input
                id="bc-phone"
                type="tel"
                name="customer_phone"
                required
                value={form.customer_phone}
                onChange={(e) => handleFormChange('customer_phone', e.target.value)}
                placeholder="05XX XXX XXXX"
                className="w-full text-sm px-3.5 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5" htmlFor="bc-service">
                Hizmet Türü
              </label>
              <select
                id="bc-service"
                name="service_type"
                value={form.service_type}
                onChange={(e) => handleFormChange('service_type', e.target.value)}
                className="w-full text-sm px-3.5 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white transition-colors cursor-pointer"
              >
                <option value="">Seçiniz...</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5" htmlFor="bc-notes">
                Açıklama / Notlar
              </label>
              <textarea
                id="bc-notes"
                name="notes"
                rows={3}
                maxLength={500}
                value={form.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Tesisat sorununuzu kısaca açıklayın..."
                className="w-full text-sm px-3.5 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white transition-colors resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{form.notes.length}/500</p>
            </div>
          </div>

          <div className="flex gap-2.5 mt-5">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex-1 py-3 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Geri
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-[2] py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <i className="ri-calendar-check-line"></i> Randevuyu Onayla
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
